import {
  createBlankMapDocument,
  createDefaultProjectConfig,
  createGalleryMapDocument,
  createPlatformerMapDocument,
  createVillageMapDocument,
  mergePosesById,
  migrateMapDocument,
  NEUTRAL_BIPED_DEFINITION,
  NEUTRAL_BIPED_POSE_LIBRARY,
  type CharacterDefinition,
  type CharacterPose,
  type CharacterPoseLibrary,
  type MapDocument,
  type Sprite2D,
  type StudioAssetRecord,
  type StudioProject,
  type StudioProjectConfig,
  type StudioProjectMeta,
  type StudioProjectTemplate,
} from '../../engine';
import { buildKitMapPersistence } from './buildKitPersistence';
import { apiDelete, apiGet, apiPatch, apiPost, apiPut } from '../services/api';
import { saveModeDocument, type ModeDocKind } from '../services/docs';

export interface ProjectSeed {
  config: StudioProjectConfig;
  map: MapDocument;
  characters: CharacterDefinition[];
  poseLibraries: CharacterPoseLibrary[];
  /** Ordered bone ids per rig, for any character beyond the built-in neutral biped. */
  rigBones?: Record<string, string[]>;
  /** Mode documents (e.g. Pixel Studio's own 'pixel' doc) to pre-populate so
   * the corresponding editor mode opens showing the same starter content
   * already seeded into `map`, rather than a blank canvas that would
   * overwrite it on the first "Save to assets". */
  modeDocuments?: Partial<Record<ModeDocKind, Record<string, unknown>>>;
}

export interface CreateProjectInput {
  ownerUid: string;
  name: string;
  template: StudioProjectTemplate;
  /** Game dimension chosen at creation; persisted in the project config. */
  projection?: '2D' | '3D';
  seed?: ProjectSeed | ((projectId: string) => ProjectSeed);
}

function blankSeed(projectId: string): ProjectSeed {
  return {
    config: createDefaultProjectConfig(),
    map: createBlankMapDocument(projectId),
    characters: [NEUTRAL_BIPED_DEFINITION],
    poseLibraries: [NEUTRAL_BIPED_POSE_LIBRARY],
    rigBones: {},
  };
}

/** Mirrors editor-pixel.tsx's own default `PIXEL_PALETTES`, so a seeded
 * 'pixel' doc looks exactly like a freshly opened, never-saved one. */
const VILLAGE_PIXEL_PALETTES = [
  { name: 'Midnight', colours: ['#0e1457', '#2142d4', '#4468f6', '#71b4fe', '#bbe5fe', '#988af6', '#eef1fb', '#3c3c6f'] },
  { name: 'Ember', colours: ['#2b1a12', '#7a2e1c', '#c75c2e', '#e8975c', '#f4d27c', '#fdf3d8'] },
  { name: 'Verdant', colours: ['#11271c', '#1f5436', '#3c8c54', '#7fce8e', '#c5ecc0', '#f2f7ee'] },
];

/** A blank, transparent 24x24 player sprite, matching Pixel Studio's own
 * default player sprite for a never-edited project. */
function villagePlayerSprite(): Sprite2D {
  return { id: 'player_idle', size: 24, cells: Array(24 * 24).fill(null) };
}

/** A Pixel Studio sprite doc's editing shape (one 'default' clip) for a
 * compiled Sprite2D - the inverse of editor-pixel.tsx's `compileSpriteDoc`. */
function pixelSpriteDocFromSprite2D(sprite: Sprite2D) {
  return {
    size: sprite.size,
    clips: { default: { frames: [sprite.cells, ...(sprite.frames ?? [])], fps: sprite.fps ?? 6, loop: true } },
    activeClip: 'default',
  };
}

/**
 * Build the 'pixel' mode document a creator would see on opening Pixel
 * Studio, from the exact same `Tilemap2D` seeded into the map - so the two
 * surfaces agree from the start and "Save to assets" is a safe no-op rather
 * than one silently overwriting the other with blank content.
 */
function villagePixelDoc(): Record<string, unknown> {
  const tilemap = createVillageMapDocument('village-preview').tilemap2d!;
  const sprites: Record<string, unknown> = { player_idle: pixelSpriteDocFromSprite2D(villagePlayerSprite()) };
  for (const sprite of tilemap.sprites ?? []) sprites[sprite.id] = pixelSpriteDocFromSprite2D(sprite);
  return {
    view: 'tilemap',
    map: tilemap.cells,
    backgroundMap: Array(tilemap.width * tilemap.height).fill(null),
    foregroundMap: Array(tilemap.width * tilemap.height).fill(null),
    mapWidth: tilemap.width,
    mapHeight: tilemap.height,
    colour: '#71b4fe',
    palettes: VILLAGE_PIXEL_PALETTES,
    activePal: 0,
    sprites,
    player: 'player_idle',
    tiles: tilemap.tiles,
    entities: (tilemap.entities ?? []).map((e) => ({
      id: e.id, spriteId: e.spriteId, x: e.x, y: e.y, blocking: e.blocking ?? false, kind: e.kind ?? 'decoration',
    })),
    // Round-trips the tilemap's own seeded spawn (and any triggers) back
    // into the editing doc - without this, opening Pixel Studio on a fresh
    // template project and clicking "Save to assets" without changing
    // anything would silently wipe the seeded spawn back to unset (editor-
    // pixel.tsx's own local `spawn` state has nothing to hydrate from
    // otherwise), the exact bug `saveToAssets`'s own spawn fix closed for a
    // hand-authored map.
    spawn: tilemap.spawn ?? null,
    triggers: [],
  };
}

function villageSeed(projectId: string): ProjectSeed {
  return {
    config: createDefaultProjectConfig(),
    map: createVillageMapDocument(projectId),
    characters: [NEUTRAL_BIPED_DEFINITION],
    poseLibraries: [NEUTRAL_BIPED_POSE_LIBRARY],
    rigBones: {},
    modeDocuments: { pixel: villagePixelDoc() },
  };
}

/** Mirrors `villagePixelDoc` for the Platformer template's own tilemap - see
 * that function's own doc comment for why `spawn`/`triggers` round-trip. */
function platformerPixelDoc(): Record<string, unknown> {
  const tilemap = createPlatformerMapDocument('platformer-preview').tilemap2d!;
  const sprites: Record<string, unknown> = { player_idle: pixelSpriteDocFromSprite2D(villagePlayerSprite()) };
  for (const sprite of tilemap.sprites ?? []) sprites[sprite.id] = pixelSpriteDocFromSprite2D(sprite);
  return {
    view: 'tilemap',
    map: tilemap.cells,
    backgroundMap: Array(tilemap.width * tilemap.height).fill(null),
    foregroundMap: Array(tilemap.width * tilemap.height).fill(null),
    mapWidth: tilemap.width,
    mapHeight: tilemap.height,
    mode: 'platformer',
    colour: '#71b4fe',
    palettes: VILLAGE_PIXEL_PALETTES,
    activePal: 0,
    sprites,
    player: 'player_idle',
    tiles: tilemap.tiles,
    entities: [],
    spawn: tilemap.spawn ?? null,
    triggers: [],
  };
}

function platformerSeed(projectId: string): ProjectSeed {
  return {
    config: createDefaultProjectConfig(),
    map: createPlatformerMapDocument(projectId),
    characters: [NEUTRAL_BIPED_DEFINITION],
    poseLibraries: [NEUTRAL_BIPED_POSE_LIBRARY],
    rigBones: {},
    modeDocuments: { pixel: platformerPixelDoc() },
  };
}

function gallerySeed(projectId: string): ProjectSeed {
  return {
    config: createDefaultProjectConfig(),
    map: createGalleryMapDocument(projectId),
    characters: [NEUTRAL_BIPED_DEFINITION],
    poseLibraries: [NEUTRAL_BIPED_POSE_LIBRARY],
    rigBones: {},
  };
}

const TEMPLATE_SEEDS: Record<StudioProjectTemplate, (projectId: string) => ProjectSeed> = {
  blank: blankSeed,
  gallery: gallerySeed,
  village: villageSeed,
  platformer: platformerSeed,
};

interface RosterWire {
  characters: CharacterDefinition[];
  rigBones: Record<string, string[]>;
}

/** A project's own custom characters/rigs, beyond the built-in neutral biped. */
export async function loadCharacterRoster(projectId: string): Promise<RosterWire> {
  return apiGet<RosterWire>(`/projects/${projectId}/characters/roster`);
}

export async function saveCharacterRoster(
  projectId: string,
  characters: CharacterDefinition[],
  rigBones: Record<string, string[]>,
  opts?: { keepalive?: boolean },
): Promise<void> {
  await apiPut(`/projects/${projectId}/characters/roster`, { characters, rigBones }, opts);
}

/** A project's authored poses (mirrors Animation Studio's own `GET
 * .../characters/poses`), flat and ungrouped - see
 * `poseLibrariesFromSavedPoses` to fold them into `CharacterPoseLibrary[]`. */
export async function loadCharacterPoses(projectId: string): Promise<CharacterPose[]> {
  return apiGet<CharacterPose[]>(`/projects/${projectId}/characters/poses`);
}

/** The project's full character list: the built-in neutral biped plus its
 * own custom roster, in the order `<GameRuntime>`'s `characters` prop and
 * `PlacedCharacters` expect. */
export function charactersFromRoster(customCharacters: CharacterDefinition[] = []): CharacterDefinition[] {
  return [NEUTRAL_BIPED_DEFINITION, ...customCharacters];
}

/**
 * Fold a project's saved poses (`loadCharacterPoses`) into `CharacterPoseLibrary[]`,
 * grouped by rig - the shape `<GameRuntime>`'s `poseLibraries` prop and
 * `PlacedCharacters` match a placed character against (by `rigId`).
 *
 * The neutral biped rig always starts from its built-in defaults
 * (`NEUTRAL_BIPED_POSE_LIBRARY`) so an older project whose creation predates
 * this rig's poses being persisted still poses correctly; `mergePosesById`
 * layers any saved poses for that rig on top (a re-saved default pose
 * overrides in place, same semantics Animation Studio already relies on).
 * Any other rig id present in `savedPoses` becomes its own library, built
 * entirely from what was saved - there is no built-in default for a custom
 * rig to fall back to.
 */
export function poseLibrariesFromSavedPoses(savedPoses: CharacterPose[] = []): CharacterPoseLibrary[] {
  const merged = mergePosesById(NEUTRAL_BIPED_POSE_LIBRARY.poses, savedPoses);
  const byRig = new Map<string, CharacterPose[]>();
  for (const pose of merged) {
    const existing = byRig.get(pose.rigId);
    if (existing) existing.push(pose);
    else byRig.set(pose.rigId, [pose]);
  }
  return [...byRig.entries()].map(([rigId, poses]) =>
    rigId === NEUTRAL_BIPED_POSE_LIBRARY.rigId
      ? { ...NEUTRAL_BIPED_POSE_LIBRARY, poses }
      : { id: `${rigId}-poses`, rigId, poses },
  );
}

interface ProjectWire {
  id: string;
  name: string;
  tenant_id: string;
  config?: Partial<StudioProjectConfig> | null;
  created_at: string;
  updated_at: string;
}

/** Wire shape shared by `GET /projects/{id}/assets` (authenticated, editor)
 * and `GET /play/{slug}/assets` (public, the published play surface) -
 * `assetFromWire` below maps either straight to the engine's
 * `StudioAssetRecord`. */
export interface AssetWire {
  id: string;
  name: string;
  cf_url: string;
  content_type: string;
  file_size: number;
  meta?: { bounds?: { size?: unknown; center?: unknown }; durationSeconds?: unknown } | null;
  created_at: string;
}

function isVec3(value: unknown): value is [number, number, number] {
  return Array.isArray(value) && value.length === 3 && value.every((n) => typeof n === 'number');
}

function assetKind(contentType: string): StudioAssetRecord['kind'] {
  if (contentType.startsWith('model/')) return 'glb';
  if (contentType.startsWith('audio/')) return 'audio';
  return 'texture';
}

/** Map an API asset row to the engine's StudioAssetRecord shape. */
export function assetFromWire(wire: AssetWire): StudioAssetRecord {
  const size = wire.meta?.bounds?.size;
  const center = wire.meta?.bounds?.center;
  const duration = wire.meta?.durationSeconds;
  return {
    id: wire.id,
    name: wire.name,
    kind: assetKind(wire.content_type),
    storagePath: wire.cf_url,
    downloadUrl: wire.cf_url,
    bounds: {
      size: isVec3(size) ? size : [1, 1, 1],
      center: isVec3(center) ? center : [0, 0, 0],
    },
    category: 'structural',
    sizeBytes: wire.file_size,
    createdAt: new Date(wire.created_at).getTime(),
    durationSeconds: typeof duration === 'number' && Number.isFinite(duration) ? duration : undefined,
  };
}

// Configs ride along on the list payload; cached here so loadProject can merge
// the persisted config over the client defaults without a second request.
const configCache = new Map<string, Partial<StudioProjectConfig>>();

/** List a user's projects, newest first. */
export async function listProjects(_ownerUid?: string): Promise<StudioProjectMeta[]> {
  const data = await apiGet<ProjectWire[]>('/projects');
  for (const p of data) {
    if (p.config) configCache.set(p.id, p.config);
  }
  return data.map((p) => ({
    id: p.id,
    name: p.name,
    ownerUid: p.tenant_id,
    template: 'blank' as StudioProjectTemplate, // fallback
    createdAt: new Date(p.created_at).getTime(),
    updatedAt: new Date(p.updated_at).getTime(),
    coverUrl: p.config?.cover?.url ?? null,
  }));
}

/** Create a project: writes the meta doc plus the map and characters documents. */
export async function createProject(input: CreateProjectInput): Promise<StudioProjectMeta> {
  const p = await apiPost<ProjectWire>('/projects', {
    name: input.name,
    config: input.projection ? { projection: input.projection } : {},
  });
  
  const projectId = p.id;
  const seed = typeof input.seed === 'function'
    ? input.seed(projectId)
    : input.seed ?? (TEMPLATE_SEEDS[input.template] ?? blankSeed)(projectId);

  // Save map from seed to API
  await apiPut(`/projects/${projectId}/map`, { map_data: seed.map });

  // Pre-populate any editor mode documents the template seeds (e.g. Pixel
  // Studio's own doc for a 2D starter), so the editor shows the same content
  // already live in the map rather than a blank one that would overwrite it.
  const modeDocEntries = Object.entries(seed.modeDocuments ?? {}) as [ModeDocKind, Record<string, unknown>][];
  if (modeDocEntries.length > 0) {
    await Promise.all(modeDocEntries.map(([kind, data]) => saveModeDocument(projectId, kind, data)));
  }

  // Save default character poses if seed has any
  const poses = seed.poseLibraries.flatMap(lib => lib.poses.map(pose => ({
    id: pose.id,
    name: pose.name,
    rigId: lib.rigId,
    boneTransforms: pose.boneTransforms
  })));

  if (poses.length > 0) {
    await apiPost(`/projects/${projectId}/characters/poses`, { poses });
  }

  // Persist any custom characters/rigs beyond the built-in neutral biped.
  const customCharacters = seed.characters.filter((c) => c.id !== NEUTRAL_BIPED_DEFINITION.id);
  if (customCharacters.length > 0) {
    await saveCharacterRoster(projectId, customCharacters, seed.rigBones ?? {});
  }

  // Create initial baseline snapshot
  await apiPost(`/projects/${projectId}/snapshots`, {
    name: 'Initial',
    map_data: seed.map
  });

  return {
    id: p.id,
    name: p.name,
    ownerUid: p.tenant_id,
    template: input.template,
    createdAt: new Date(p.created_at).getTime(),
    updatedAt: new Date(p.updated_at).getTime(),
  };
}

/** Load a full project (meta + config + map + characters + assets). */
export async function loadProject(projectId: string): Promise<StudioProject | null> {
  try {
    const projects = await listProjects();
    const meta = projects.find((p) => p.id === projectId);
    if (!meta) return null;

    const [mapData, assets, roster, poses] = await Promise.all([
      apiGet<MapDocument>(`/projects/${projectId}/map`),
      apiGet<AssetWire[]>(`/projects/${projectId}/assets`),
      loadCharacterRoster(projectId),
      loadCharacterPoses(projectId),
    ]);

    // Normalise legacy documents (e.g. deprecated `interactiveObjects`, stale
    // `version`) up front so project.map is current before any full-document
    // PUT re-persists it - otherwise saveStructure would write back the
    // deprecated shape.
    return {
      meta,
      config: { ...createDefaultProjectConfig(), ...(configCache.get(projectId) ?? {}) },
      map: mapData ? migrateMapDocument(mapData) : createBlankMapDocument(projectId),
      characters: charactersFromRoster(roster?.characters),
      poseLibraries: poseLibrariesFromSavedPoses(poses ?? []),
      rigBones: roster?.rigBones ?? {},
      assets: assets.map(assetFromWire),
    };
  } catch (error) {
    console.error('Failed to load project:', error);
    return null;
  }
}

/** List a project's uploaded assets. */
export async function listProjectAssets(projectId: string): Promise<StudioAssetRecord[]> {
  const rows = await apiGet<AssetWire[]>(`/projects/${projectId}/assets`);
  return rows.map(assetFromWire);
}

/**
 * Persist structural map changes (floors/walls/settings), not just instances.
 * Routed through the engine's own `saveDocument` (a queued GET-merge-PUT, see
 * studioApiPersistence.ts) rather than a raw PUT here: a raw PUT would race
 * the engine's own autosave/manual-save queue against the same map document -
 * whichever write lands last on the server wins outright, silently
 * discarding the other (e.g. a Map Builder floor edit landing between an
 * Edit-mode autosave read and write drops the autosave's props, or vice
 * versa).
 */
export async function saveMapDocument(projectId: string, map: MapDocument): Promise<void> {
  await buildKitMapPersistence.saveDocument(projectId, map);
}

export async function saveProjectConfig(
  projectId: string,
  config: StudioProjectConfig,
  opts?: { keepalive?: boolean },
): Promise<void> {
  configCache.set(projectId, config);
  await apiPatch(`/projects/${projectId}`, { config }, undefined, opts);
}

export async function renameProject(projectId: string, name: string): Promise<void> {
  await apiPatch(`/projects/${projectId}`, { name });
}

/**
 * Duplicate a project server-side: the API clones the project row, map, mode
 * documents, character roster/poses and asset references directly (reusing
 * the source's own S3 objects rather than re-uploading), so - unlike
 * export-then-import (`sstkArchive.ts`) - no uploaded binary is dropped.
 */
export async function duplicateProject(projectId: string, name?: string): Promise<StudioProjectMeta> {
  const p = await apiPost<ProjectWire>(`/projects/${projectId}/duplicate`, name ? { name } : {});
  return {
    id: p.id,
    name: p.name,
    ownerUid: p.tenant_id,
    template: 'blank',
    createdAt: new Date(p.created_at).getTime(),
    updatedAt: new Date(p.updated_at).getTime(),
    // The duplicate endpoint clones config (including any cover image) onto
    // the new project - map it the same way listProjects does, so a
    // duplicate's dashboard card shows the source's real cover immediately
    // instead of falling back to the generated placeholder until the next
    // full project list reload.
    coverUrl: p.config?.cover?.url ?? null,
  };
}

export async function touchProject(_projectId: string): Promise<void> {
  // Handled automatically by database trigger / backend updates
}

export async function saveAssetRecord(_projectId: string, _asset: StudioAssetRecord): Promise<void> {
  // Handled during glbUpload confirmation phase
}

/** Delete a project. */
export async function deleteProject(projectId: string): Promise<void> {
  await apiDelete(`/projects/${projectId}`);
}

/** Rename an uploaded asset. */
export async function renameAsset(projectId: string, assetId: string, name: string): Promise<void> {
  await apiPatch(`/projects/${projectId}/assets/${assetId}`, { name });
}

/** Delete an uploaded asset (record and stored file). */
export async function deleteAsset(projectId: string, assetId: string): Promise<void> {
  await apiDelete(`/projects/${projectId}/assets/${assetId}`);
}

/* ── Scene snapshots (version history beyond publish versions) ─────────── */

export interface SceneSnapshotMeta {
  id: string;
  name: string | null;
  created_at: string;
}

export async function listSceneSnapshots(projectId: string): Promise<SceneSnapshotMeta[]> {
  return apiGet<SceneSnapshotMeta[]>(`/projects/${projectId}/snapshots`);
}

export async function createSceneSnapshot(projectId: string, name: string, map: MapDocument): Promise<void> {
  await apiPost(`/projects/${projectId}/snapshots`, { name, map_data: map });
}

export async function loadSceneSnapshot(projectId: string, snapshotId: string): Promise<MapDocument> {
  return apiGet<MapDocument>(`/projects/${projectId}/snapshots/${snapshotId}`);
}
